import Box from '@risk-smart/themed-cloudscape-components/box';
import Container from '@risk-smart/themed-cloudscape-components/container';
import Header from '@risk-smart/themed-cloudscape-components/header';
import { Separator } from '@risksmart-app/atomic-ui';
import { Fragment } from 'react';

import type { RequestHistoryEntry } from './useResponseRequestHistory';

export interface ResponseRequestHistoryViewProps {
  entries: RequestHistoryEntry[];
  lang: {
    title: string;
    unknownAuthor: string;
  };
}

/**
 * Newest request first — the hook orders the list, this only renders it.
 *
 * Entries are divided by a rule rather than by spacing alone: a request is
 * free text that can run to several paragraphs, and with whitespace as the
 * only boundary two consecutive requests read as one. The separator sits
 * BETWEEN entries (never after the last), so the block does not end on a rule
 * with nothing under it.
 */
export const ResponseRequestHistoryView = ({
  entries,
  lang,
}: ResponseRequestHistoryViewProps) => (
  <Container header={<Header variant={'h2'}>{lang.title}</Header>}>
    <div className={'flex flex-col gap-4'}>
      {entries.map((entry, index) => (
        <Fragment key={entry.id}>
          {index > 0 ? <Separator /> : null}
          <div className={'flex flex-col gap-1'}>
            <div className={'flex flex-wrap items-baseline gap-2'}>
              <Box fontWeight={'bold'}>
                {entry.authorName ?? lang.unknownAuthor}
              </Box>
              <Box color={'text-status-inactive'}>{entry.timestamp}</Box>
            </div>
            {/* Reviewers type multi-line reasons; without pre-wrap the newlines
                collapse and a paragraphed request reads as one run-on line.
                `break-words` stops an unbroken paste (a URL, an ID) widening
                the container past the page. */}
            <div className={'whitespace-pre-wrap break-words'}>
              {entry.content}
            </div>
          </div>
        </Fragment>
      ))}
    </div>
  </Container>
);
